// Smart contract utilities for atomic piece trading
// This file contains the logic for handling atomic trades using Solana smart contracts

import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL, ComputeBudgetProgram } from '@solana/web3.js';
import { 
  getAssociatedTokenAddress, 
  createApproveInstruction,
  createRevokeInstruction,
  getAccount,
  TOKEN_PROGRAM_ID
} from '@solana/spl-token';
import { supabase } from '../lib/supabase';
import { getRpcEndpoint } from '../config/rpc';

// Platform wallet for delegate-based transfers (same as your marketplace wallet)
export const PLATFORM_WALLET_ADDRESS = '8cm597KjGmwTSzadrcVCHVpqwzdRZy1BM2yUrDDEpJcB';
const PLATFORM_DELEGATE = new PublicKey(PLATFORM_WALLET_ADDRESS);
const TRADE_COMPLETION_FEE_SOL = 0.05; // Fee for completing a trade (charged to initiator only when trade executes)

// Helper to get RPC connection (uses Helius for reliability)
const getConnection = () => {
  const rpcUrl = getRpcEndpoint();
  console.log('🌐 Trade contract using RPC:', rpcUrl.includes('helius') ? 'Helius (premium)' : 'Public');
  return new Connection(rpcUrl, 'confirmed');
};

// Trade states
export const TRADE_STATES = {
  PENDING: 'pending',
  INITIATOR_CONFIRMED: 'initiator_confirmed',
  COUNTERPARTY_CONFIRMED: 'counterparty_confirmed',
  BOTH_CONFIRMED: 'both_confirmed',
  EXECUTED: 'executed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired'
};

/**
 * Create a new P2P trade request in database (goes into trade_requests table first)
 * @param {Object} wallet - Connected wallet
 * @param {Object} tradeData - Trade details
 * @returns {Promise<Object>} Result with requestId
 */
export const createTradeOnChain = async (wallet, tradeData) => {
  try {
    console.log('📝 Creating P2P trade request...');
    console.log('Requester:', wallet.publicKey.toString());
    console.log('Target:', tradeData.counterparty);
    console.log('Offered pieces:', tradeData.initiatorPieces.length);
    console.log('Requested pieces:', tradeData.counterpartyPieces?.length || 0);

    // Validate inputs
    if (!isValidSolanaAddress(tradeData.counterparty)) {
      throw new Error('Invalid counterparty wallet address');
    }

    if (!tradeData.initiatorPieces || tradeData.initiatorPieces.length === 0) {
      throw new Error('Must include at least one piece to offer');
    }

    if (!tradeData.counterpartyPieces || tradeData.counterpartyPieces.length === 0) {
      throw new Error('Must include at least one piece to request');
    }

    // Validate all pieces exist in database
    console.log('🔍 Validating pieces exist in database...');
    const allPieces = [...tradeData.initiatorPieces, ...tradeData.counterpartyPieces];
    for (const piece of allPieces) {
      if (piece.mint_address || piece.mintAddress) {
        const mintAddress = piece.mint_address || piece.mintAddress;
        const { data: dbPiece, error: dbError } = await supabase
          .from('pieces')
          .select('id, name')
          .eq('mint_address', mintAddress)
          .maybeSingle();
        
        if (dbError || !dbPiece) {
          throw new Error(`Piece "${piece.name}" (${mintAddress.slice(0, 8)}...) not found in database. Cannot create trade.`);
        }
        console.log(`✅ Validated: ${piece.name}`);
      }
    }

    // Prepare trade request data for database
    const requestRecord = {
      requester_wallet: wallet.publicKey.toString(),
      target_wallet: tradeData.counterparty,
      target_piece: {}, // Legacy field - using requested_pieces instead
      offered_pieces: tradeData.initiatorPieces.map(p => ({
        mint_address: p.mint_address || p.mintAddress,
        name: p.name,
        price: p.price,
        category: p.category,
        image_url: p.imageUrl || p.thumbnail?.dataUrl,
      })),
      requested_pieces: tradeData.counterpartyPieces.map(p => ({
        mint_address: p.mint_address || p.mintAddress,
        name: p.name,
        price: p.price,
        category: p.category,
        image_url: p.imageUrl || p.thumbnail?.dataUrl,
      })),
      message: tradeData.message || '',
      expires_at: tradeData.expiresAt || calculateExpirationTime(3), // Default 3 days
      status: 'pending', // Waiting for target to accept/decline
    };

    // Insert trade request into database
    const { data, error } = await supabase
      .from('trade_requests')
      .insert([requestRecord])
      .select()
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to create trade request: ${error.message}`);
    }

    const requestId = data.id;

    console.log('✅ Trade request created:', requestId);
    console.log('ℹ️  Target wallet will see this in their "Incoming Trades"');
    console.log('ℹ️  Once accepted, you can execute the trade and pay the 0.05 SOL fee');

    return {
      success: true,
      requestId,
      data,
    };
  } catch (error) {
    console.error('Error creating trade request:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Accept a trade request and create p2p_trade entry with automatic counterparty confirmation
 * @param {Object} wallet - Connected wallet
 * @param {string} requestId - Trade request UUID
 * @returns {Promise<Object>} Result with tradeId
 */
export const acceptTradeRequest = async (wallet, requestId) => {
  try {
    console.log('✅ Accepting trade request:', requestId);
    
    // Get trade request details
    const { data: request, error: requestError } = await supabase
      .from('trade_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();

    if (requestError || !request) {
      throw new Error(`Trade request not found: ${requestError?.message}`);
    }

    // Verify user is the target
    const walletAddress = wallet.publicKey.toString();
    if (request.target_wallet !== walletAddress) {
      throw new Error('You are not the target of this trade request');
    }

    // Check status
    if (request.status !== 'pending') {
      throw new Error(`Trade request is not pending. Current status: ${request.status}`);
    }

    console.log('🎨 Setting delegate approvals for your pieces...');
    
    // Get connection
    const connection = getConnection();
    
    // Get counterparty pieces (pieces the acceptor needs to set delegates for)
    const counterpartyPieces = request.requested_pieces || [];
    
    if (counterpartyPieces.length > 0) {
      // First verify database ownership for all pieces
      console.log('🔍 Verifying piece ownership in database...');
      for (const piece of counterpartyPieces) {
        if (!piece.mint_address) continue;
        
        const { data: dbPiece, error: dbError } = await supabase
          .from('pieces')
          .select('holder_wallet, mint_address, name, creator_wallet')
          .eq('mint_address', piece.mint_address)
          .maybeSingle();
        
        if (dbError) {
          console.error('Database error:', dbError);
          throw new Error(`Database error checking piece "${piece.name}": ${dbError.message}`);
        }
        
        if (!dbPiece) {
          throw new Error(`Piece "${piece.name}" not found in database. Cannot accept trade.`);
        }
        
        // Check if the current user owns this piece (either as holder or creator if not yet sold)
        const isHolder = dbPiece.holder_wallet === walletAddress;
        const isCreatorWithUnsoldPiece = dbPiece.creator_wallet === walletAddress && !dbPiece.holder_wallet;
        
        if (!isHolder && !isCreatorWithUnsoldPiece) {
          const actualOwner = dbPiece.holder_wallet || dbPiece.creator_wallet || 'Unknown';
          throw new Error(`You don't own "${piece.name}" (Owner: ${actualOwner.slice(0, 8)}...). Cannot accept trade.`);
        }
        
        console.log(`✅ Database ownership verified for: ${piece.name}`);
      }
      
      // Create transaction to set delegate authorities for counterparty's pieces
      const transaction = new Transaction();
      
      for (const piece of counterpartyPieces) {
        if (!piece.mint_address) continue;

        const mintPubkey = new PublicKey(piece.mint_address);
        const tokenAccount = await getAssociatedTokenAddress(
          mintPubkey,
          wallet.publicKey,
          true
        );

        // Verify on-chain ownership
        try {
          const accountInfo = await getAccount(connection, tokenAccount);
          if (accountInfo.amount < 1n) {
            throw new Error(`Token account for "${piece.name}" exists but has 0 balance`);
          }
          console.log(`✅ On-chain ownership verified for: ${piece.name}`);
        } catch (error) {
          // More specific error handling
          if (error.message?.includes('could not find account') || error.name === 'TokenAccountNotFoundError') {
            throw new Error(`Token account for "${piece.name}" not found. The NFT may have been recently transferred - please wait a moment and try again.`);
          }
          throw new Error(`Cannot verify ownership of "${piece.name}": ${error.message}`);
        }

        // Add approve instruction (delegate to platform wallet)
        transaction.add(
          createApproveInstruction(
            tokenAccount,
            PLATFORM_DELEGATE,
            wallet.publicKey,
            1
          )
        );

        console.log(`✅ Delegate approval added for: ${piece.name}`);
      }

      // Send delegate approval transaction
      if (transaction.instructions.length > 0) {
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
        transaction.recentBlockhash = blockhash;
        transaction.lastValidBlockHeight = lastValidBlockHeight;
        transaction.feePayer = wallet.publicKey;

        // Pre-simulate transaction before sending to wallet (Phantom best practice)
        console.log('🔍 Simulating delegate approval transaction...');
        try {
          const simulation = await connection.simulateTransaction(transaction, {
            sigVerify: false,  // Don't verify signatures in simulation
            commitment: 'confirmed',
            replaceRecentBlockhash: true,
          });
          
          if (simulation.value.err) {
            console.warn('⚠️ Simulation warning:', simulation.value.err);
            console.warn('⚠️ Continuing despite simulation warning (transaction may still succeed)');
          } else {
            console.log('✅ Pre-simulation passed - transaction should succeed');
          }
        } catch (simError) {
          // Don't fail if simulation fails - wallet will do its own preflight
          console.warn('⚠️ Pre-simulation failed (non-fatal):', simError.message);
        }

        console.log('📤 Sending delegate approval transaction...');
        const result = await wallet.sendTransaction(transaction, connection, {
          skipPreflight: false,  // Still let wallet do preflight check
          preflightCommitment: 'confirmed'
        });
        const signature = typeof result === 'string' ? result : (result?.signature || result?.txid || result?.transactionSignature || result);
        
        console.log('⏳ Confirming transaction...');
        
        // Use polling-based confirmation for faster results on mainnet
        const maxWaitTime = 60000; // 60 seconds max
        const startTime = Date.now();
        const pollInterval = 1500; // Check every 1.5 seconds
        
        let confirmed = false;
        
        // Try standard confirmation with short timeout (10s)
        try {
          await Promise.race([
            connection.confirmTransaction({
              signature,
              blockhash,
              lastValidBlockHeight,
            }, 'confirmed').then(() => { confirmed = true; }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Switching to polling')), 10000)
            )
          ]);
          if (confirmed) {
            console.log('✅ Delegate approvals confirmed (standard):', signature);
          }
        } catch (confirmError) {
          console.log('⚠️ Using polling confirmation for faster results...');
        }
        
        // If not confirmed yet, use polling
        if (!confirmed) {
          while (Date.now() - startTime < maxWaitTime) {
            try {
              const status = await connection.getSignatureStatus(signature);
              if (status?.value?.confirmationStatus === 'confirmed' || status?.value?.confirmationStatus === 'finalized') {
                console.log('✅ Delegate approvals confirmed (polling):', signature);
                confirmed = true;
                break;
              }
              if (status?.value?.err) {
                throw new Error(`Transaction failed: ${JSON.stringify(status.value.err)}`);
              }
            } catch (pollError) {
              // Ignore polling errors, keep trying
            }
            
            if (Date.now() - startTime >= maxWaitTime) {
              // Last resort: check if transaction actually exists
              try {
                const tx = await connection.getTransaction(signature, { commitment: 'confirmed' });
                if (tx) {
                  console.log('✅ Delegate approvals confirmed (verified):', signature);
                  confirmed = true;
                  break;
                }
              } catch (finalError) {
                // Transaction not found
              }
              
              if (!confirmed) {
                throw new Error(`Transaction confirmation timed out after ${maxWaitTime/1000}s. Transaction may still be processing - signature: ${signature}`);
              }
            }
            
            await new Promise(resolve => setTimeout(resolve, pollInterval));
          }
        }
      }
    }

    // Create p2p_trade entry with counterparty already confirmed
    // Set 3-day execution deadline from acceptance
    const now = new Date();
    const confirmedExpiresAt = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000); // 3 days from now
    
    const tradeRecord = {
      initiator_wallet: request.requester_wallet,
      counterparty_wallet: request.target_wallet,
      initiator_pieces: request.offered_pieces,
      counterparty_pieces: request.requested_pieces,
      message: request.message || '',
      expires_at: request.expires_at,
      status: 'counterparty_confirmed', // Counterparty confirmed by accepting
      initiator_confirmed: false, // Initiator still needs to confirm
      counterparty_confirmed: true, // Auto-confirmed when accepting
      counterparty_confirmed_at: now.toISOString(),
      counterparty_delegates_set: counterpartyPieces.length > 0,
      confirmed_expires_at: confirmedExpiresAt.toISOString(), // 3-day deadline starts now
    };

    const { data: trade, error: tradeError } = await supabase
      .from('p2p_trades')
      .insert([tradeRecord])
      .select()
      .maybeSingle();

    if (tradeError) {
      throw new Error(`Failed to create trade: ${tradeError.message}`);
    }

    const tradeId = trade.id;

    // Update trade request status
    await supabase
      .from('trade_requests')
      .update({
        status: 'accepted',
        trade_id: tradeId,
        responded_at: new Date().toISOString(),
      })
      .eq('id', requestId);

    // Update trade link
    const tradeLink = `${window.location.origin}/trade-pieces?trade=${tradeId}`;
    await supabase
      .from('p2p_trades')
      .update({ trade_link: tradeLink })
      .eq('id', tradeId);

    // Log activity
    await supabase.from('trade_activity').insert({
      trade_id: tradeId,
      activity_type: 'accepted',
      wallet: walletAddress,
      metadata: {
        request_id: requestId,
        initiator_pieces: request.offered_pieces.length,
        counterparty_pieces: request.requested_pieces.length,
        auto_confirmed: true,
      },
    });

    console.log('✅ Trade accepted and you are confirmed!');
    console.log('ℹ️  Initiator must now confirm their pieces');
    console.log('ℹ️  After initiator confirms, they can execute and pay 0.05 SOL fee');

    return {
      success: true,
      tradeId,
      tradeLink,
      data: trade,
    };
  } catch (error) {
    console.error('Error accepting trade request:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Decline a trade request
 * @param {Object} wallet - Connected wallet
 * @param {string} requestId - Trade request UUID
 * @param {string} reason - Optional decline reason
 * @returns {Promise<Object>} Result
 */
export const declineTradeRequest = async (wallet, requestId, reason = '') => {
  try {
    console.log('❌ Declining trade request:', requestId);
    
    // Get trade request details
    const { data: request, error: requestError } = await supabase
      .from('trade_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();

    if (requestError || !request) {
      throw new Error(`Trade request not found: ${requestError?.message}`);
    }

    // Verify user is the target
    const walletAddress = wallet.publicKey.toString();
    if (request.target_wallet !== walletAddress) {
      throw new Error('You are not the target of this trade request');
    }

    // Update trade request status
    await supabase
      .from('trade_requests')
      .update({
        status: 'declined',
        responded_at: new Date().toISOString(),
        response_message: reason,
      })
      .eq('id', requestId);

    console.log('✅ Trade request declined');

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error declining trade request:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Cancel a trade request (before acceptance)
 * @param {Object} wallet - Connected wallet
 * @param {string} requestId - Trade request UUID
 * @returns {Promise<Object>} Result
 */
export const cancelTradeRequest = async (wallet, requestId) => {
  try {
    console.log('❌ Cancelling trade request:', requestId);
    
    // Get trade request details
    const { data: request, error: requestError } = await supabase
      .from('trade_requests')
      .select('*')
      .eq('id', requestId)
      .maybeSingle();

    if (requestError || !request) {
      throw new Error(`Trade request not found: ${requestError?.message}`);
    }

    // Verify user is the requester
    const walletAddress = wallet.publicKey.toString();
    if (request.requester_wallet !== walletAddress) {
      throw new Error('You are not the requester of this trade request');
    }

    // Can only cancel pending requests
    if (request.status !== 'pending') {
      throw new Error(`Cannot cancel trade request with status: ${request.status}`);
    }

    // Update trade request status
    await supabase
      .from('trade_requests')
      .update({
        status: 'cancelled',
      })
      .eq('id', requestId);

    console.log('✅ Trade request cancelled');

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error cancelling trade request:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Confirm a trade and set delegate authority for NFT transfers
 * Both parties must confirm before trade can be executed
 * @param {Object} wallet - Connected wallet
 * @param {string} tradeId - Trade UUID
 * @returns {Promise<Object>} Result
 */
export const confirmTradeOnChain = async (wallet, tradeId) => {
  try {
    console.log('✅ Confirming trade:', tradeId);
    console.log('Wallet:', wallet.publicKey.toString());

    // Get trade details
    const { data: trade, error: tradeError } = await supabase
      .from('p2p_trades')
      .select('*')
      .eq('id', tradeId)
      .maybeSingle();

    if (tradeError || !trade) {
      throw new Error(`Trade not found: ${tradeError?.message || 'Unknown error'}`);
    }

    // Check if trade is still valid
    if (trade.status === 'expired' || new Date(trade.expires_at) < new Date()) {
      throw new Error('Trade has expired');
    }

    if (trade.status === 'executed') {
      throw new Error('Trade has already been executed');
    }

    if (trade.status === 'cancelled') {
      throw new Error('Trade has been cancelled');
    }

    // Determine if user is initiator or counterparty
    const walletAddress = wallet.publicKey.toString();
    const isInitiator = trade.initiator_wallet === walletAddress;
    const isCounterparty = trade.counterparty_wallet === walletAddress;

    if (!isInitiator && !isCounterparty) {
      throw new Error('You are not part of this trade');
    }

    // Check if delegates are already set (allow re-confirmation if delegates weren't set)
    if (isInitiator && trade.initiator_delegates_set) {
      throw new Error('You have already set delegates for this trade');
    }
    if (isCounterparty && trade.counterparty_delegates_set) {
      throw new Error('You have already set delegates for this trade');
    }

    // Get pieces that this user needs to set delegates for
    const userPieces = isInitiator ? trade.initiator_pieces : trade.counterparty_pieces;

    if (!userPieces || userPieces.length === 0) {
      throw new Error('No pieces to trade from your side');
    }

    // Validate piece limits to ensure transaction size stays within Solana limits
    const totalPieces = trade.initiator_pieces.length + trade.counterparty_pieces.length;
    if (totalPieces > 10) {
      throw new Error(`This trade has too many pieces (${totalPieces} total). Maximum is 10 total pieces across both parties.`);
    }

    console.log(`🎨 Setting delegates for ${userPieces.length} pieces...`);
    console.log(`🎨 Total pieces in trade: ${totalPieces}`);

    // Create transaction to set delegate authority for all pieces
    const connection = getConnection();
    const transaction = new Transaction();

    // Add compute budget instruction for larger trades
    const computeUnits = 200000 + (userPieces.length * 50000);
    console.log(`⚙️ Setting compute units to ${computeUnits} for ${userPieces.length} pieces`);
    
    transaction.add(
      ComputeBudgetProgram.setComputeUnitLimit({
        units: computeUnits,
      })
    );

    // Add small priority fee to ensure transaction processes quickly
    transaction.add(
      ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 10000,
      })
    );

    for (const piece of userPieces) {
      if (!piece.mint_address) {
        throw new Error(`Piece ${piece.name} is missing mint address`);
      }

      const mintPubkey = new PublicKey(piece.mint_address);
      const tokenAccount = await getAssociatedTokenAddress(
        mintPubkey,
        wallet.publicKey,
        true // allowOwnerOffCurve
      );

      // Verify user owns the NFT
      console.log(`🔍 Verifying ownership of ${piece.name}`);
      console.log(`  Mint: ${piece.mint_address}`);
      console.log(`  Your wallet: ${wallet.publicKey.toString()}`);
      console.log(`  Token account: ${tokenAccount.toString()}`);
      
      try {
        const accountInfo = await getAccount(connection, tokenAccount);
        console.log(`  ✅ Token account found`);
        console.log(`  Amount: ${accountInfo.amount.toString()}`);
        
        if (accountInfo.amount < 1n) {
          console.error(`  ❌ Token account exists but has 0 balance`);
          throw new Error(`You don't own "${piece.name}". The NFT may have been transferred or sold.`);
        }
        
        console.log(`  ✅ Ownership verified for ${piece.name}`);
      } catch (error) {
        console.error(`❌ Ownership verification failed for ${piece.name}:`, error);
        console.error(`Error name: ${error.name}`);
        console.error(`Error message: ${error.message}`);
        
        // If the error is "account not found", it means the token account doesn't exist = user doesn't own this NFT
        if (error.name === 'TokenAccountNotFoundError' || error.message.includes('could not find')) {
          throw new Error(`You don't own "${piece.name}". The token account doesn't exist. The NFT may have been transferred, sold, or never minted to your wallet.`);
        }
        
        // Re-throw the original error if it's something else
        throw error;
      }

      // Add approve instruction (delegate to platform wallet)
      transaction.add(
        createApproveInstruction(
          tokenAccount, // token account
          PLATFORM_DELEGATE, // delegate
          wallet.publicKey, // owner
          1 // amount (NFTs are always 1)
        )
      );

      console.log(`✅ Delegate approval added for: ${piece.name}`);
    }

    // Get recent blockhash
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;
    transaction.feePayer = wallet.publicKey;

    // Send and confirm transaction
    console.log('📤 Sending delegate approval transaction...');
    const result = await wallet.sendTransaction(transaction, connection);
    const signature = typeof result === 'string' ? result : (result?.signature || result?.txid || result?.transactionSignature || result);
    
    console.log('⏳ Confirming transaction...');
    await connection.confirmTransaction({
      signature,
      blockhash,
      lastValidBlockHeight,
    });

    console.log('✅ Delegate approvals confirmed:', signature);

    // Update trade confirmation in database
    const updates = {
      updated_at: new Date().toISOString(),
    };

    if (isInitiator) {
      updates.initiator_confirmed = true;
      updates.initiator_confirmed_at = new Date().toISOString();
      updates.initiator_delegates_set = true;
      
      // Update status
      if (trade.counterparty_confirmed) {
        updates.status = 'both_confirmed';
        // Set 3-day execution deadline when both parties confirm
        updates.confirmed_expires_at = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
      } else {
        updates.status = 'initiator_confirmed';
      }
    } else {
      updates.counterparty_confirmed = true;
      updates.counterparty_confirmed_at = new Date().toISOString();
      updates.counterparty_delegates_set = true;
      
      // Update status
      if (trade.initiator_confirmed) {
        updates.status = 'both_confirmed';
        // Set 3-day execution deadline when both parties confirm
        updates.confirmed_expires_at = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
      } else {
        updates.status = 'counterparty_confirmed';
      }
    }

    await supabase
      .from('p2p_trades')
      .update(updates)
      .eq('id', tradeId);

    // Log activity
    await supabase.from('trade_activity').insert({
      trade_id: tradeId,
      activity_type: 'confirmed',
      wallet: walletAddress,
      transaction_signature: signature,
      metadata: {
        role: isInitiator ? 'initiator' : 'counterparty',
        pieces_count: userPieces.length,
      },
    });

    console.log('✅ Trade confirmation recorded');

    return {
      success: true,
      signature,
      status: updates.status,
      bothConfirmed: updates.status === 'both_confirmed',
    };
  } catch (error) {
    console.error('Error confirming trade:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Execute atomic trade - calls edge function to perform the swap
 * Both parties must have confirmed before execution
 * @param {Object} wallet - Connected wallet
 * @param {string} tradeId - Trade UUID
 * @returns {Promise<Object>} Result with transaction signature
 */
export const executeAtomicTrade = async (wallet, tradeId) => {
  try {
    console.log('⚡ Executing atomic trade:', tradeId);
    console.log('Executor:', wallet.publicKey.toString());

    // Get trade details to verify status
    const { data: trade, error: tradeError } = await supabase
      .from('p2p_trades')
      .select('*')
      .eq('id', tradeId)
      .maybeSingle();

    if (tradeError || !trade) {
      throw new Error(`Trade not found: ${tradeError?.message}`);
    }

    // Verify trade is ready for execution
    // Accept either 'both_confirmed' status OR both flags being true
    const bothConfirmed = trade.initiator_confirmed && trade.counterparty_confirmed;
    
    if (!bothConfirmed && trade.status !== 'both_confirmed') {
      throw new Error(`Trade not ready for execution. Both parties must confirm first. Status: ${trade.status}, Initiator: ${trade.initiator_confirmed}, Counterparty: ${trade.counterparty_confirmed}`);
    }
    
    if (!trade.initiator_confirmed || !trade.counterparty_confirmed) {
      throw new Error('Both parties must confirm before execution');
    }

    // Check expiration
    if (new Date(trade.expires_at) < new Date()) {
      throw new Error('Trade has expired');
    }

    // Charge 0.05 SOL trade completion fee from initiator
    const executorWallet = wallet.publicKey.toString();
    const isInitiator = trade.initiator_wallet === executorWallet;
    
    console.log('💰 Fee Payment Check:');
    console.log(`  Executor: ${executorWallet}`);
    console.log(`  Initiator: ${trade.initiator_wallet}`);
    console.log(`  Is Initiator: ${isInitiator}`);
    console.log(`  Fee: ${TRADE_COMPLETION_FEE_SOL} SOL`);
    console.log(`  Platform Wallet: ${PLATFORM_WALLET_ADDRESS}`);
    
    // Only initiator pays the fee
    if (isInitiator) {
      // FINAL check: Ensure trade hasn't been executed by someone else
      console.log('🔒 Final trade status check before charging fee...');
      const { data: finalTradeCheck, error: finalTradeError } = await supabase
        .from('p2p_trades')
        .select('status, initiator_confirmed, counterparty_confirmed')
        .eq('id', tradeId)
        .maybeSingle();
      
      if (finalTradeError || !finalTradeCheck) {
        throw new Error('⚠️ Trade not found. It may have been cancelled or already executed. No fee was charged.');
      }
      
      if (finalTradeCheck.status === 'completed') {
        throw new Error('⚠️ This trade has already been executed by another party. No fee was charged. Please refresh the page.');
      }
      
      if (finalTradeCheck.status === 'cancelled' || finalTradeCheck.status === 'expired') {
        throw new Error(`⚠️ Trade is ${finalTradeCheck.status}. No fee was charged.`);
      }
      
      const bothStillConfirmed = finalTradeCheck.initiator_confirmed && finalTradeCheck.counterparty_confirmed;
      if (!bothStillConfirmed) {
        throw new Error('⚠️ Trade confirmations have changed. Both parties must still be confirmed. No fee was charged.');
      }
      
      console.log('✅ Final trade check passed - proceeding with fee payment');
      
      const connection = getConnection();
      
      // Check initiator's balance
      const balance = await connection.getBalance(wallet.publicKey);
      const balanceSOL = balance / LAMPORTS_PER_SOL;
      
      console.log(`  Current Balance: ${balanceSOL.toFixed(4)} SOL`);
      
      if (balanceSOL < TRADE_COMPLETION_FEE_SOL) {
        throw new Error(`Insufficient balance for trade completion fee. Need ${TRADE_COMPLETION_FEE_SOL} SOL but only have ${balanceSOL.toFixed(4)} SOL`);
      }
      
      // Create transaction to pay trade completion fee
      const feeLamports = Math.floor(TRADE_COMPLETION_FEE_SOL * LAMPORTS_PER_SOL);
      console.log(`  Transferring ${feeLamports} lamports (${TRADE_COMPLETION_FEE_SOL} SOL)`);
      
      const feeTransaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: wallet.publicKey,
          toPubkey: new PublicKey(PLATFORM_WALLET_ADDRESS),
          lamports: feeLamports,
        })
      );
      
      // Get recent blockhash
      const { blockhash } = await connection.getLatestBlockhash('confirmed');
      feeTransaction.recentBlockhash = blockhash;
      feeTransaction.feePayer = wallet.publicKey;
      
      // Send fee payment transaction
      console.log('📤 Sending fee payment transaction...');
      const feeResult = await wallet.sendTransaction(feeTransaction, connection, {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      });
      const feeSignature = typeof feeResult === 'string' ? feeResult : (feeResult?.signature || feeResult?.txid || feeResult?.transactionSignature || feeResult);
      
      console.log('⏳ Confirming fee payment...');
      console.log(`  Transaction signature: ${feeSignature}`);
      await connection.confirmTransaction(feeSignature, 'confirmed');
      console.log('✅ Trade completion fee PAID!');
      console.log(`  Amount: ${TRADE_COMPLETION_FEE_SOL} SOL`);
      console.log(`  To: ${PLATFORM_WALLET_ADDRESS}`);
      console.log(`  Signature: ${feeSignature}`);
      console.log(`  Explorer: https://solscan.io/tx/${feeSignature}`);
      
      // Log fee payment activity
      await supabase.from('trade_activity').insert({
        trade_id: tradeId,
        activity_type: 'fee_paid',
        wallet: executorWallet,
        transaction_signature: feeSignature,
        metadata: {
          fee_amount_sol: TRADE_COMPLETION_FEE_SOL,
          fee_type: 'trade_completion',
          platform_wallet: PLATFORM_WALLET_ADDRESS,
        },
      });
    } else {
      console.log('ℹ️ Counterparty executing - no fee charged');
    }

    // Call edge function to execute the trade
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/execute-p2p-trade`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        tradeId,
        executorWallet: wallet.publicKey.toString(),
      }),
    });

    // Check if response is ok before parsing JSON
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge function error response:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        console.error('Edge function error details:', {
          error: errorJson.error,
          errorName: errorJson.errorName,
          errorDetails: errorJson.errorDetails,
        });
        const errorMsg = errorJson.error || errorJson.errorDetails || `Edge function error: ${response.status}`;
        throw new Error(errorMsg);
      } catch (parseError) {
        throw new Error(`Edge function error (${response.status}): ${errorText.substring(0, 200)}`);
      }
    }

    const result = await response.json();

    if (!result.success) {
      console.error('Edge function returned error:', {
        error: result.error,
        errorName: result.errorName,
        errorDetails: result.errorDetails,
      });
      const errorMsg = result.error || result.errorDetails || 'Failed to execute trade';
      throw new Error(errorMsg);
    }

    console.log('✅ Trade executed successfully!');
    console.log('Transaction:', result.signature);
    console.log('Explorer:', result.explorerUrl);
    if (isInitiator) {
      console.log('💵 Fee paid:', `${TRADE_COMPLETION_FEE_SOL} SOL`);
    }

    return {
      success: true,
      signature: result.signature,
      explorerUrl: result.explorerUrl,
      feePaid: isInitiator ? TRADE_COMPLETION_FEE_SOL : 0,
      feeCharged: isInitiator,
    };
  } catch (error) {
    console.error('Error executing atomic trade:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Cancel a trade and optionally revoke delegate authorities
 * @param {Object} wallet - Connected wallet
 * @param {string} tradeId - Trade UUID
 * @param {boolean} revokeDelegates - Whether to revoke delegate authorities
 * @param {string} reason - Cancellation reason (optional)
 * @returns {Promise<Object>} Result
 */
export const cancelTradeOnChain = async (wallet, tradeId, revokeDelegates = false, reason = '') => {
  try {
    console.log('❌ Cancelling trade:', tradeId);
    
    // Get trade details
    const { data: trade, error: tradeError } = await supabase
      .from('p2p_trades')
      .select('*')
      .eq('id', tradeId)
      .maybeSingle();

    if (tradeError || !trade) {
      throw new Error(`Trade not found: ${tradeError?.message}`);
    }

    // Verify user is part of the trade
    const walletAddress = wallet.publicKey.toString();
    if (trade.initiator_wallet !== walletAddress && trade.counterparty_wallet !== walletAddress) {
      throw new Error('You are not part of this trade');
    }

    // Can't cancel if already executed
    if (trade.status === 'executed') {
      throw new Error('Cannot cancel an executed trade');
    }

    // Can't cancel if already cancelled
    if (trade.status === 'cancelled') {
      throw new Error('Trade is already cancelled');
    }

    // Revoke delegates if requested
    let revokeSignature = null;
    if (revokeDelegates) {
      const isInitiator = trade.initiator_wallet === walletAddress;
      const userPieces = isInitiator ? trade.initiator_pieces : trade.counterparty_pieces;
      
      if (userPieces && userPieces.length > 0) {
        console.log(`🔒 Revoking delegates for ${userPieces.length} pieces...`);
        
        const connection = getConnection();
        const transaction = new Transaction();

        for (const piece of userPieces) {
          if (!piece.mint_address) continue;

          const mintPubkey = new PublicKey(piece.mint_address);
          const tokenAccount = await getAssociatedTokenAddress(
            mintPubkey,
            wallet.publicKey,
            true
          );

          // Add revoke instruction
          transaction.add(
            createRevokeInstruction(
              tokenAccount, // token account
              wallet.publicKey // owner
            )
          );
        }

        if (transaction.instructions.length > 0) {
          const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
          transaction.recentBlockhash = blockhash;
          transaction.lastValidBlockHeight = lastValidBlockHeight;
          transaction.feePayer = wallet.publicKey;

          const revokeResult = await wallet.sendTransaction(transaction, connection);
          revokeSignature = typeof revokeResult === 'string' ? revokeResult : (revokeResult?.signature || revokeResult?.txid || revokeResult?.transactionSignature || revokeResult);
          await connection.confirmTransaction({
            signature: revokeSignature,
            blockhash,
            lastValidBlockHeight,
          });

          console.log('✅ Delegates revoked:', revokeSignature);
        }
      }
    }

    // Update trade status in database
    await supabase
      .from('p2p_trades')
      .update({
        status: 'cancelled',
        cancelled_by: walletAddress,
        cancelled_at: new Date().toISOString(),
        cancellation_reason: reason,
      })
      .eq('id', tradeId);

    // Log activity
    await supabase.from('trade_activity').insert({
      trade_id: tradeId,
      activity_type: 'cancelled',
      wallet: walletAddress,
      transaction_signature: revokeSignature,
      metadata: {
        reason,
        revoked_delegates: revokeDelegates,
      },
    });

    console.log('✅ Trade cancelled');

    return {
      success: true,
      signature: revokeSignature,
    };
  } catch (error) {
    console.error('Error cancelling trade:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get trade details from database
 * @param {string} tradeId - Trade UUID
 * @returns {Promise<Object>} Trade data
 */
export const getTradeDetails = async (tradeId) => {
  try {
    const { data, error } = await supabase
      .from('p2p_trades')
      .select('*')
      .eq('id', tradeId)
      .maybeSingle();

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      trade: data,
    };
  } catch (error) {
    console.error('Error getting trade details:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get all trades for a wallet
 * @param {string} walletAddress - Wallet address
 * @param {string} status - Optional status filter
 * @returns {Promise<Object>} Trades array
 */
export const getWalletTrades = async (walletAddress, status = null) => {
  try {
    let query = supabase
      .from('p2p_trades')
      .select('*')
      .or(`initiator_wallet.eq.${walletAddress},counterparty_wallet.eq.${walletAddress}`)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      trades: data || [],
    };
  } catch (error) {
    console.error('Error getting wallet trades:', error);
    return {
      success: false,
      error: error.message,
      trades: [],
    };
  }
};

// Validate wallet address
export const isValidSolanaAddress = (address) => {
  try {
    if (!address || typeof address !== 'string') {
      return false;
    }
    new PublicKey(address);
    return true;
  } catch {
    return false;
  }
};

// Calculate trade expiration time
export const calculateExpirationTime = (days = 3) => {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
};

// Format time remaining
export const formatTimeRemaining = (expiresAt) => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry - now;
  
  if (diff <= 0) return 'Expired';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

// Get trade fee amount
export const getTradeFee = () => {
  return TRADE_FEE_SOL;
};

// Get revenue wallet address
export const getRevenueWalletAddress = () => {
  return REVENUE_WALLET_ADDRESS;
};

/**
 * Check and auto-cancel expired confirmed trades (3-day deadline)
 * This function should be called periodically to clean up expired trades
 * @returns {Promise<Object>} Result with count of cancelled trades
 */
export const checkAndCancelExpiredTrades = async () => {
  try {
    console.log('🔍 Checking for expired confirmed trades...');
    
    // Query for confirmed trades (counterparty_confirmed or both_confirmed) that have passed their 3-day deadline
    const { data: expiredTrades, error } = await supabase
      .from('p2p_trades')
      .select('id, initiator_wallet, counterparty_wallet, confirmed_expires_at, status')
      .in('status', ['counterparty_confirmed', 'both_confirmed'])
      .not('confirmed_expires_at', 'is', null)
      .lt('confirmed_expires_at', new Date().toISOString());

    if (error) {
      console.error('❌ Error checking expired trades:', error);
      throw new Error(error.message);
    }

    if (!expiredTrades || expiredTrades.length === 0) {
      console.log('✅ No expired confirmed trades found');
      return {
        success: true,
        cancelled_count: 0,
        cancelled_trades: [],
      };
    }

    console.log(`⚠️ Found ${expiredTrades.length} expired confirmed trade(s), cancelling...`);

    // Cancel each expired trade
    const cancelResults = [];
    for (const trade of expiredTrades) {
      try {
        const { error: updateError } = await supabase
          .from('p2p_trades')
          .update({
            status: 'expired',
            updated_at: new Date().toISOString(),
            cancellation_reason: 'Automatically expired - trade not executed within 3 days',
          })
          .eq('id', trade.id);

        if (updateError) {
          console.error(`❌ Error expiring trade ${trade.id}:`, updateError);
        } else {
          console.log(`✅ Auto-expired trade ${trade.id}`);
          cancelResults.push({
            id: trade.id,
            initiator_wallet: trade.initiator_wallet,
            counterparty_wallet: trade.counterparty_wallet,
          });

          // Log activity
          await supabase.from('trade_activity').insert({
            trade_id: trade.id,
            activity_type: 'expired',
            wallet: 'system', // System-triggered expiration
            metadata: {
              reason: 'auto_expired_execution_deadline',
              expired_at: trade.confirmed_expires_at,
            },
          });
        }
      } catch (err) {
        console.error(`❌ Exception expiring trade ${trade.id}:`, err);
      }
    }

    return {
      success: true,
      cancelled_count: cancelResults.length,
      cancelled_trades: cancelResults,
    };
  } catch (error) {
    console.error('❌ Error in checkAndCancelExpiredTrades:', error);
    return {
      success: false,
      error: error.message,
      cancelled_count: 0,
      cancelled_trades: [],
    };
  }
};
